import React, { useState, useEffect } from 'react';
import { adminApi } from '../lib/api';
import { useToast } from '../hooks/use-toast';
import { Check, X, PackageCheck, Truck } from 'lucide-react';

const STATUSES = [
  { value: '', label: 'Tutti' },
  { value: 'pending', label: 'In attesa' },
  { value: 'received', label: 'Ricevuti' },
  { value: 'approved', label: 'Approvati' },
  { value: 'rejected', label: 'Rifiutati' },
  { value: 'completed', label: 'Completati' },
];

const FLOW_STEPS = [
  { value: 'pending', label: 'In attesa' },
  { value: 'received', label: 'Ricevuto' },
  { value: 'approved', label: 'Approvato' },
  { value: 'completed', label: 'Completato' },
];

const statusColor = (status) => {
  switch (status) {
    case 'received': return 'bg-[#B8860B] text-white';
    case 'approved': return 'bg-[#4A5D23] text-white';
    case 'completed': return 'bg-green-600 text-white';
    case 'rejected': return 'bg-[#C05746] text-white';
    default: return 'bg-[#E5E0D8] text-[#57534E]';
  }
};

const StatusStepper = ({ tradeIn }) => {
  if (tradeIn.status === 'rejected') return null;
  const currentIdx = FLOW_STEPS.findIndex((s) => s.value === tradeIn.status);
  return (
    <div className="flex items-center gap-0 mt-4" data-testid="tradein-stepper">
      {FLOW_STEPS.map((step, i) => (
        <React.Fragment key={step.value}>
          <div className="flex flex-col items-center">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] ${
              i <= currentIdx ? 'bg-[#4A5D23] text-white' : 'bg-[#E5E0D8] text-[#78716C]'
            }`}>
              {i < currentIdx ? <Check className="w-3 h-3" /> : i + 1}
            </div>
            <span className={`text-[10px] mt-1 ${i <= currentIdx ? 'text-[#1C1917]' : 'text-[#78716C]'}`}>
              {step.label}
            </span>
          </div>
          {i < FLOW_STEPS.length - 1 && (
            <div className={`h-[2px] w-10 mb-4 ${i < currentIdx ? 'bg-[#4A5D23]' : 'bg-[#E5E0D8]'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

const RejectModal = ({ tradeIn, onConfirm, onCancel, submitting }) => {
  const [reason, setReason] = useState('');
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="reject-modal">
      <div className="bg-white max-w-md w-full p-6">
        <h3 className="text-lg font-medium mb-2">Rifiuta reso</h3>
        <p className="text-sm text-[#57534E] mb-4">
          Il motivo verrà inviato al cliente via email e notifica. Campo obbligatorio.
        </p>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Es. Il capo presenta danni non riparabili non conformi alle condizioni del programma..."
          rows={4}
          className="w-full border border-[#E5E0D8] p-3 text-sm focus:outline-none focus:border-[#1C1917]"
          data-testid="reject-reason-input"
        />
        <div className="flex justify-end gap-2 mt-4">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm bg-[#F2EFE9] hover:bg-[#E5E0D8] transition-colors"
            data-testid="reject-cancel-btn"
          >
            Annulla
          </button>
          <button
            onClick={() => onConfirm(reason)}
            disabled={!reason.trim() || submitting}
            className="px-4 py-2 text-sm bg-[#C05746] text-white hover:bg-[#a84637] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="reject-confirm-btn"
          >
            {submitting ? 'Invio...' : 'Conferma rifiuto'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function AdminTradeInsPage() {
  const { toast } = useToast();
  const [tradeIns, setTradeIns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [rejecting, setRejecting] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchTradeIns();
  }, [filter]);

  const fetchTradeIns = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getTradeIns(filter || undefined);
      setTradeIns(response.data.trade_ins);
    } catch (error) {
      console.error('Error fetching trade-ins:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (tradeInId, newStatus, reason) => {
    setSubmitting(true);
    try {
      await adminApi.updateTradeInStatus(tradeInId, newStatus, reason);
      toast({ title: 'Stato aggiornato' });
      setRejecting(null);
      fetchTradeIns();
    } catch (error) {
      toast({
        title: 'Errore',
        description: error.response?.data?.detail || 'Aggiornamento non riuscito',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
        <h2 className="text-2xl font-light">Gestione Biolosophy Value</h2>
        <div className="flex flex-wrap gap-2">
          {STATUSES.map((status) => (
            <button
              key={status.value}
              onClick={() => setFilter(status.value)}
              data-testid={`filter-tradein-${status.value || 'all'}`}
              className={`px-4 py-2 text-sm transition-colors ${
                filter === status.value
                  ? 'bg-[#1C1917] text-white'
                  : 'bg-[#F2EFE9] hover:bg-[#E5E0D8]'
              }`}
            >
              {status.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-24 bg-[#E5E0D8] animate-pulse" />
          ))}
        </div>
      ) : tradeIns.length === 0 ? (
        <div className="text-center py-16 bg-[#F2EFE9]">
          <p className="text-[#57534E]">Nessuna richiesta Biolosophy Value trovata</p>
        </div>
      ) : (
        <div className="space-y-4">
          {tradeIns.map((tradeIn, idx) => (
            <div key={tradeIn.trade_in_id} className="bg-[#F2EFE9] p-6" data-testid={`admin-tradein-${idx}`}>
              <div className="flex flex-wrap gap-6">
                {tradeIn.product_image && (
                  <img
                    src={tradeIn.product_image}
                    alt={tradeIn.product_name || 'Prodotto'}
                    className="w-24 h-32 object-cover flex-shrink-0"
                    data-testid={`tradein-product-image-${idx}`}
                  />
                )}

                <div className="flex-1 min-w-[240px]">
                  <p className="font-medium text-lg" data-testid={`tradein-product-name-${idx}`}>
                    {tradeIn.product_name || tradeIn.product_id}
                  </p>
                  <p className="font-mono text-xs text-[#78716C] mt-0.5">{tradeIn.trade_in_id}</p>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2 mt-3 text-sm">
                    <div>
                      <span className="text-[#78716C] text-xs uppercase tracking-wider block">Cliente</span>
                      <span data-testid={`tradein-customer-${idx}`}>{tradeIn.customer_name || '-'}</span>
                      <span className="block text-xs text-[#57534E]">{tradeIn.customer_email}</span>
                    </div>
                    <div>
                      <span className="text-[#78716C] text-xs uppercase tracking-wider block">Taglia / Colore</span>
                      <span>{tradeIn.size || '-'}{tradeIn.color ? ` / ${tradeIn.color}` : ''}</span>
                    </div>
                    <div>
                      <span className="text-[#78716C] text-xs uppercase tracking-wider block">Anni di possesso</span>
                      <span>{tradeIn.years_owned != null ? tradeIn.years_owned : '-'}</span>
                    </div>
                    <div>
                      <span className="text-[#78716C] text-xs uppercase tracking-wider block">Prezzo originale</span>
                      <span>{tradeIn.original_price != null ? `${tradeIn.original_price.toFixed(2)}€` : '-'}</span>
                    </div>
                    <div>
                      <span className="text-[#78716C] text-xs uppercase tracking-wider block">Sconto</span>
                      <span className="text-[#4A5D23] font-medium" data-testid={`tradein-discount-${idx}`}>
                        {tradeIn.discount_percentage || 0}%
                      </span>
                    </div>
                    <div>
                      <span className="text-[#78716C] text-xs uppercase tracking-wider block">Richiesta il</span>
                      <span>
                        {new Date(tradeIn.created_at).toLocaleDateString('it-IT', {
                          year: 'numeric', month: 'short', day: 'numeric',
                        })}
                      </span>
                    </div>
                  </div>

                  {tradeIn.reason && (
                    <p className="text-sm mt-3 text-[#57534E]">
                      <span className="text-[#78716C] text-xs uppercase tracking-wider">Nota cliente: </span>
                      {tradeIn.reason}
                    </p>
                  )}
                  {tradeIn.rejection_reason && (
                    <p className="text-sm mt-2 text-[#C05746]" data-testid={`tradein-rejection-reason-${idx}`}>
                      <span className="text-xs uppercase tracking-wider">Motivo rifiuto: </span>
                      {tradeIn.rejection_reason}
                    </p>
                  )}

                  <StatusStepper tradeIn={tradeIn} />
                </div>

                <div className="flex flex-col items-end gap-2">
                  <span className={`px-3 py-1 text-sm ${statusColor(tradeIn.status)}`} data-testid={`tradein-status-${idx}`}>
                    {STATUSES.find((s) => s.value === tradeIn.status)?.label || tradeIn.status}
                  </span>

                  {tradeIn.status === 'pending' && (
                    <div className="flex flex-col items-end gap-2 mt-2">
                      <button
                        onClick={() => handleStatusChange(tradeIn.trade_in_id, 'received')}
                        data-testid={`receive-tradein-${idx}`}
                        className="flex items-center gap-1 px-4 py-2 bg-[#B8860B] text-white hover:bg-[#9a7009] transition-colors"
                      >
                        <Truck className="w-4 h-4" />
                        Capo ricevuto
                      </button>
                      <button
                        onClick={() => setRejecting(tradeIn.trade_in_id)}
                        data-testid={`reject-tradein-${idx}`}
                        className="flex items-center gap-1 px-4 py-2 bg-[#C05746] text-white hover:bg-[#a84637] transition-colors"
                      >
                        <X className="w-4 h-4" />
                        Rifiuta
                      </button>
                    </div>
                  )}

                  {tradeIn.status === 'received' && (
                    <div className="flex flex-col items-end gap-2 mt-2">
                      <button
                        onClick={() => handleStatusChange(tradeIn.trade_in_id, 'approved')}
                        data-testid={`approve-tradein-${idx}`}
                        className="flex items-center gap-1 px-4 py-2 bg-[#4A5D23] text-white hover:bg-[#3B4A1C] transition-colors"
                      >
                        <Check className="w-4 h-4" />
                        Approva
                      </button>
                      <button
                        onClick={() => setRejecting(tradeIn.trade_in_id)}
                        data-testid={`reject-tradein-${idx}`}
                        className="flex items-center gap-1 px-4 py-2 bg-[#C05746] text-white hover:bg-[#a84637] transition-colors"
                      >
                        <X className="w-4 h-4" />
                        Rifiuta
                      </button>
                    </div>
                  )}

                  {tradeIn.status === 'approved' && (
                    <button
                      onClick={() => handleStatusChange(tradeIn.trade_in_id, 'completed')}
                      data-testid={`complete-tradein-${idx}`}
                      className="flex items-center gap-1 px-4 py-2 bg-green-600 text-white hover:bg-green-700 transition-colors mt-2"
                    >
                      <PackageCheck className="w-4 h-4" />
                      Segna come Completato
                    </button>
                  )}
                </div>
              </div>

              {tradeIn.status_history?.length > 0 && (
                <div className="mt-4 pt-3 border-t border-[#E5E0D8] text-xs text-[#78716C]" data-testid={`tradein-history-${idx}`}>
                  {tradeIn.status_history.map((h, hi) => (
                    <span key={hi}>
                      {hi > 0 && ' → '}
                      {STATUSES.find((s) => s.value === h.status)?.label || h.status}{' '}
                      ({new Date(h.at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })})
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {rejecting && (
        <RejectModal
          tradeIn={rejecting}
          submitting={submitting}
          onConfirm={(reason) => handleStatusChange(rejecting, 'rejected', reason)}
          onCancel={() => setRejecting(null)}
        />
      )}
    </div>
  );
}
